import { Component, computed, inject, signal } from '@angular/core';
import { BaseModalComponent } from "../../../../../../core/components/modals/base-modal/base-modal.component";
import { CarouselComponent } from "../../../../../../shared/components/ui/carousel/carousel.component";
import {
  FileUploadCardComponent
} from "../../../../../../shared/components/ui/file-upload-card/file-upload-card.component";
import { defaultDesignBlockData } from "../base-design-block-modal/configs/default-design-block-data";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { DesignBlockData } from "../base-design-block-modal/types/base-design-block.types";
import { AttachFileParams } from "../../../../../../core/types/file.types";
import { AttachProductCardFileParams, GetFilesParams } from "../../../types/product-card-files.types";
import { ListingFilesStateService } from "./services/listing-files-state.service";
import { ListingFilesModalService } from "./services/listing-files-modal.service";
import { listingEntityFileType, listingKeyParams, titleModal } from "./configs/listing-files-modal-config";
import { ListingFileData, ListingFilesDialogData } from "./types/listing-file-data.types";
import { LoadingService } from "../../../../../../core/services/loading.service";
import { EditModeService } from "../../../../../../core/services/edit-mode.service";
import { ListingShowFileLibraryStateService } from "./services/listing-show-file-library-state.service";
import { LfHeaderModalComponent } from "./components/lf-header-modal/lf-header-modal.component";
import { LfFileCardComponent } from "./components/lf-file-card/lf-file-card.component";
import { ListingFileLibraryComponent } from "./components/listing-file-library/listing-file-library.component";
import { LfBottomModalComponent } from "./components/lf-bottom-modal/lf-bottom-modal.component";
import { LoaderComponent } from "../../../../../../shared/components/loader/loader.component";
import { ListingPathStateService } from "./services/listing-path-state.service";

@Component({
  selector: 'app-listing-files-modal',
  imports: [
    BaseModalComponent,
    CarouselComponent,
    FileUploadCardComponent,
    LfHeaderModalComponent,
    LfFileCardComponent,
    ListingFileLibraryComponent,
    LfBottomModalComponent,
    LoaderComponent
  ],
  templateUrl: './listing-files-modal.component.html',
  styleUrl: './listing-files-modal.component.scss',
  providers: [ LoadingService, EditModeService ]
})
export class ListingFilesModalComponent extends BaseModalComponent {
  public designBlockFilesDialogData = inject<ListingFilesDialogData>(MAT_DIALOG_DATA);
  public listingFilesStateService: ListingFilesStateService = inject(ListingFilesStateService);
  private listingFilesModalService: ListingFilesModalService = inject(ListingFilesModalService);
  private listingShowFileLibraryStateService: ListingShowFileLibraryStateService = inject(ListingShowFileLibraryStateService);
  private listingPathStateService: ListingPathStateService = inject(ListingPathStateService);

  private uploadQueue: Array<AttachFileParams> = [];
  public isShowFileLibrary = computed(() => this.listingShowFileLibraryStateService.isShowFileLibrary());
  public remainingFileSlots = computed(() => this.listingFilesStateService.getRemainingFileSlots());
  public modalKey = computed(() => this.designBlockFilesDialogData.modalKey);
  public designBlockData = signal<DesignBlockData>(defaultDesignBlockData);
  public isActiveUploadFile: boolean = false;
  public currentIndex: number = 0;
  public titleModal = computed(() => titleModal[this.modalKey()]);
  public listingEntityFileType = computed(() => listingEntityFileType[this.modalKey()]);
  public listingKeyParams = computed(() => listingKeyParams[this.modalKey()]);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.modalKey());
    this.listingPathStateService.resetPathState();
    this.listingFilesStateService.resetListingFilesState();
    this.listingShowFileLibraryStateService.resetShowFileLibrary();
  }

  public onIndexChange(index: number): void {
    this.currentIndex = index;
  }

  public uploadFiles(isActiveUploadFile: boolean): void {
    this.isActiveUploadFile = isActiveUploadFile;
  }

  public async attachFile(attachFileParams: AttachFileParams): Promise<void> {
    this.uploadQueue.push(attachFileParams);

    if (this.uploadQueue.length === 1) {
      this.processUploadQueue();
    }
  }

  public async updateComment(data: DesignBlockData): Promise<void> {
    this.designBlockData.set(data);
  }

  public updateField(file: ListingFileData): void {
    this.listingFilesStateService.updateListingFilesState(file);
  }

  public async getFiles(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    const params: GetFilesParams = {
      path: this.listingPathStateService.pathState(),
      params: { [this.listingKeyParams()]: this.designBlockData().id }
    };

    await this.listingFilesModalService.getFiles(params)
      .then((data) => this.listingFilesStateService.setListingFilesState(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(this.modalKey());
    this.listingPathStateService.setPathState(this.modalKey());
    this.designBlockData.set(this.designBlockFilesDialogData[this.modalKey()] as DesignBlockData);
    await this.getFiles();
  }

  private processUploadQueue(): void {
    if (this.uploadQueue.length === 0) { return; }

    const { parentId, fileId } = this.uploadQueue[0];
    const attachFileParams: AttachProductCardFileParams = {
      path: this.listingPathStateService.pathState(),
      body: {
        fileId: fileId,
        [this.listingKeyParams()]: parentId,
        uspPriority: this.listingFilesStateService.getNextFreeUspPriority()
      }
    };

    this.listingFilesModalService.attachFile<ListingFileData>(attachFileParams)
      .then((data) => this.listingFilesStateService.updateListingFilesState(data))
      .finally(() => {
        this.uploadQueue.shift();
        this.uploadQueue.length > 0 && this.processUploadQueue();
      });
  }
}
